import React from 'react';
import { format } from 'date-fns';
import { TimeLog, ReportType } from '../../../types/timeReport';
import { parseUTCTimestamp } from '../../../utils/dateUtils';
import {
  generateDailyShifts,
  generateWeeklyBreakdown,
  generateMonthlyBreakdown,
  formatHours,
} from '../../../utils/timeReportCalculations';

interface DailyBreakdownProps {
  staffName: string;
  shifts: TimeLog[];
  reportType: ReportType;
  startDate: Date;
  endDate: Date;
  onEditShift: (shiftId: string) => void;
}

const DailyBreakdown: React.FC<DailyBreakdownProps> = ({
  staffName,
  shifts,
  reportType,
  startDate,
  endDate,
  onEditShift,
}) => {
  if (reportType === 'weekly') {
    return <WeeklyView staffName={staffName} shifts={shifts} startDate={startDate} endDate={endDate} />;
  }

  if (reportType === 'monthly') {
    return <MonthlyView staffName={staffName} shifts={shifts} startDate={startDate} />;
  }

  return <DetailedView shifts={shifts} onEditShift={onEditShift} />;
};

const WeeklyView: React.FC<{
  staffName: string;
  shifts: TimeLog[];
  startDate: Date;
  endDate: Date;
}> = ({ staffName, shifts, startDate, endDate }) => {
  const weeklyData = generateWeeklyBreakdown(shifts, startDate);
  const days = generateDailyShifts(shifts, startDate, endDate);

  return (
    <div className="space-y-4">
      <h4 className="font-medium text-gray-900">
        {staffName} - Week of {weeklyData.week_label}
      </h4>

      <div className="space-y-2">
        {days.map((day) => {
          const dayName = format(day.date, 'EEE MM/dd');
          const isOff = day.is_off;

          return (
            <div
              key={day.dateString}
              className={`flex items-center justify-between py-2 px-4 rounded ${
                isOff ? 'bg-gray-50' : 'bg-white border border-gray-200'
              }`}
            >
              <div className="flex-1">
                <span className="font-medium text-gray-700">{dayName}</span>
              </div>
              <div className="flex-1 text-center">
                {!isOff && day.shifts.length > 0 ? (
                  <span className="text-sm text-gray-600">
                    {format(parseUTCTimestamp(day.shifts[0].clock_in_time), 'h:mm a')} -{' '}
                    {day.shifts[0].clock_out_time
                      ? format(parseUTCTimestamp(day.shifts[0].clock_out_time), 'h:mm a')
                      : '???'}
                  </span>
                ) : (
                  <span className="text-sm text-gray-400">OFF</span>
                )}
              </div>
              <div className="flex-1 text-right">
                {!isOff ? (
                  <div className="inline-flex items-center">
                    <span className="font-semibold text-gray-900">
                      [{formatHours(day.total_hours)}]
                    </span>
                    {day.shifts.some((s) => s.needs_review) ? (
                      <span className="ml-2 text-yellow-600" title="Incomplete">
                        ⚠️
                      </span>
                    ) : (
                      <span className="ml-2 text-green-600" title="Complete">
                        ✅
                      </span>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      <div className="pt-4 border-t border-gray-200">
        <div className="flex justify-between text-sm">
          <span className="font-medium text-gray-700">Weekly Total:</span>
          <span className="font-bold text-gray-900">{formatHours(weeklyData.total_hours)}</span>
        </div>
        <div className="flex justify-between text-sm mt-1">
          <span className="text-gray-600">Days Worked:</span>
          <span className="text-gray-900">{weeklyData.days_worked}</span>
        </div>
      </div>
    </div>
  );
};

const MonthlyView: React.FC<{
  staffName: string;
  shifts: TimeLog[];
  startDate: Date;
}> = ({ staffName, shifts, startDate }) => {
  const [expandedWeeks, setExpandedWeeks] = React.useState<Set<number>>(new Set());

  const monthlyData = generateMonthlyBreakdown(
    shifts,
    startDate.getMonth(),
    startDate.getFullYear()
  );

  const toggleWeek = (weekIndex: number) => {
    const newExpanded = new Set(expandedWeeks);
    if (newExpanded.has(weekIndex)) {
      newExpanded.delete(weekIndex);
    } else {
      newExpanded.add(weekIndex);
    }
    setExpandedWeeks(newExpanded);
  };

  return (
    <div className="space-y-4">
      <h4 className="font-medium text-gray-900">
        {staffName} - {monthlyData.month_label}
      </h4>

      <div className="space-y-3">
        {monthlyData.weeks.map((week, index) => (
          <div key={index} className="bg-white border border-gray-200 rounded overflow-hidden">
            {/* Week Summary - Clickable */}
            <div
              className="p-3 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => toggleWeek(index)}
            >
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <span className="text-gray-500">
                    {expandedWeeks.has(index) ? '▼' : '▶'}
                  </span>
                  <div>
                    <span className="text-sm font-medium text-gray-700">
                      Week {index + 1} ({week.week_label})
                    </span>
                    <span className="ml-3 text-xs text-gray-500">
                      {week.days_worked} {week.days_worked === 1 ? 'day' : 'days'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-sm font-semibold text-gray-900">
                    {formatHours(week.total_hours)}
                  </span>
                  {week.days.some((d) => d.shifts.some((s) => s.needs_review)) && (
                    <span className="text-yellow-600" title="Has incomplete shifts">
                      ⚠️
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Expanded Daily Breakdown */}
            {expandedWeeks.has(index) && (
              <div className="border-t border-gray-200 bg-gray-50 p-3">
                <div className="space-y-2">
                  {week.days.map((day) => {
                    const dayName = format(day.date, 'EEE MM/dd');
                    const isOff = day.is_off;

                    return (
                      <div
                        key={day.dateString}
                        className={`flex items-center justify-between py-2 px-4 rounded ${
                          isOff ? 'bg-gray-100' : 'bg-white border border-gray-200'
                        }`}
                      >
                        <div className="flex-1">
                          <span className="font-medium text-gray-700 text-sm">{dayName}</span>
                        </div>
                        <div className="flex-1 text-center">
                          {!isOff && day.shifts.length > 0 ? (
                            <span className="text-xs text-gray-600">
                              {format(parseUTCTimestamp(day.shifts[0].clock_in_time), 'h:mm a')} -{' '}
                              {day.shifts[0].clock_out_time
                                ? format(parseUTCTimestamp(day.shifts[0].clock_out_time), 'h:mm a')
                                : '???'}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">OFF</span>
                          )}
                        </div>
                        <div className="flex-1 text-right">
                          {!isOff ? (
                            <div className="inline-flex items-center">
                              <span className="font-semibold text-gray-900 text-sm">
                                [{formatHours(day.total_hours)}]
                              </span>
                              {day.shifts.some((s) => s.needs_review) ? (
                                <span className="ml-2 text-yellow-600" title="Incomplete">
                                  ⚠️
                                </span>
                              ) : (
                                <span className="ml-2 text-green-600" title="Complete">
                                  ✅
                                </span>
                              )}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="pt-4 border-t border-gray-200 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="font-medium text-gray-700">Monthly Total:</span>
          <span className="font-bold text-gray-900">{formatHours(monthlyData.total_hours)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Days Worked:</span>
          <span className="text-gray-900">{monthlyData.days_worked} days</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Average:</span>
          <span className="text-gray-900">{formatHours(monthlyData.avg_hours_per_day)}/day</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Status:</span>
          <span className={monthlyData.weeks.some((w) => w.days.some((d) => d.shifts.some((s) => s.needs_review))) ? 'text-yellow-600' : 'text-green-600'}>
            {monthlyData.weeks.some((w) => w.days.some((d) => d.shifts.some((s) => s.needs_review)))
              ? '⚠️ Has incomplete shifts'
              : '✅ All shifts complete'}
          </span>
        </div>
      </div>
    </div>
  );
};

const DetailedView: React.FC<{
  shifts: TimeLog[];
  onEditShift: (shiftId: string) => void;
}> = ({ shifts, onEditShift }) => {
  return (
    <div className="space-y-4">
      <h4 className="font-medium text-gray-900">Detailed Shift Log</h4>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Date
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Clock In
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Clock Out
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Hours
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {shifts.map((shift) => {
              const clockIn = parseUTCTimestamp(shift.clock_in_time);
              const clockOut = shift.clock_out_time
                ? parseUTCTimestamp(shift.clock_out_time)
                : null;

              return (
                <tr key={shift.id} className={shift.needs_review ? 'bg-yellow-50' : ''}>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {format(clockIn, 'MM/dd/yyyy')}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {format(clockIn, 'h:mm a')}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {clockOut ? (
                      format(clockOut, 'h:mm a')
                    ) : (
                      <span className="text-red-600 font-medium">---</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                    {formatHours(shift.hours_worked || 1.0)}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {shift.needs_review ? (
                      <span className="text-yellow-600 font-medium">Defaulted</span>
                    ) : shift.admin_edited ? (
                      <span className="text-blue-600 font-medium" title={`Edited by admin: ${shift.admin_notes || ''}`}>
                        Edited ✏️
                      </span>
                    ) : (
                      <span className="text-green-600 font-medium">Complete</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {shift.needs_review && (
                      <button
                        onClick={() => onEditShift(shift.id)}
                        className="text-indigo-600 hover:text-indigo-900 font-medium mr-3"
                      >
                        Edit
                      </button>
                    )}
                    {shift.clock_in_latitude && (
                      <button
                        onClick={() => {
                          const url = `https://www.google.com/maps?q=${shift.clock_in_latitude},${shift.clock_in_longitude}`;
                          window.open(url, '_blank');
                        }}
                        className="text-blue-600 hover:text-blue-900 text-xs"
                        title="View clock-in location"
                      >
                        📍 GPS
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {shifts.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No shifts found for this period.
        </div>
      )}
    </div>
  );
};

export default DailyBreakdown;
